import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DiscountResponseDTO } from '@libs/dtos/discount/discount.dto';
import { DiscountService } from '@services/discount/discount.service';
import { ViewportService } from '@services/geo/viewport.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { DateService } from '@services/shared/date.service';
import { ConfirmDialogService } from '@services/ui/confirm-dialog.service';
import { TableBase } from '@shared/bases/table.base';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TableComponent } from '@shared/components/table/table.component';
import { TableAction, TableColumn } from '@shared/components/table/table.model';
import { SharedModule } from '@shared/modules/shared.module';
import { translateApiError } from 'app/core/utils/api-error';
import { activeLocale, formatEur } from 'app/core/utils/locale';
import { catchError, finalize, Observable, of } from 'rxjs';
import { DiscountCrudComponent } from './discount-crud/discount-crud.component';

@Component({
	selector: 'app-discounts',
	imports: [SharedModule, TableComponent, ButtonComponent, DiscountCrudComponent],
	templateUrl: './discounts.component.html',
	styleUrl: './discounts.component.scss',
})
export class DiscountsComponent extends TableBase implements OnInit {
	@ViewChild('crudDiscount') crudDiscount!: DiscountCrudComponent;

	data: DiscountResponseDTO[] = [];

	headers: TableColumn[] = [];

	override mainField = 'name';
	override sortField = this.mainField;
	override limit = 10;
	loadError = false;

	constructor(
		private discountService: DiscountService,
		private dateService: DateService,
		private messageAlertService: MessageAlertService,
		private confirmDialogService: ConfirmDialogService,
		private translocoService: TranslocoService,
		private cdr: ChangeDetectorRef,
		override viewportService: ViewportService,
	) {
		super(viewportService);

		this.headers = [
			{ field: 'name', header: this.translocoService.translate('discountsCrud.headers.name') },
			{ field: 'formattedValue', header: this.translocoService.translate('discountsCrud.headers.value') },
			{
				field: 'appliesTo',
				header: this.translocoService.translate('discountsCrud.headers.appliesTo'),
				sortable: false,
			},
			{ field: 'formattedStartDate', header: this.translocoService.translate('discountsCrud.headers.startDate') },
			{ field: 'formattedEndDate', header: this.translocoService.translate('discountsCrud.headers.endDate') },
			{ field: 'status', header: this.translocoService.translate('discountsCrud.headers.status'), type: 'tag' },
		];
	}

	override loadData(): void {
		this.loadDiscounts();
	}

	ngOnInit(): void {
		this.limit = this.isMobile ? this.DEFAULT_LIMIT_MOBILE : this.DEFAULT_LIMIT;
		this.loadDiscounts();
	}

	loadDiscounts(): void {
		this.isLoading = true;
		this.loadError = false;
		this.cdr.markForCheck();

		const params = {
			page: this.page,
			limit: this.limit,
			sort_by: this.sortField,
			sort_order: (this.sortOrder === 1 ? 'asc' : 'desc') as 'asc' | 'desc',
		};

		this.discountService
			.getDiscounts(params)
			.pipe(
				catchError((err) => {
					console.error('Error loading discounts:', err);
					this.data = [];
					this.totalRecords = 0;
					this.isLoading = false;
					this.loadError = true;
					this.cdr.detectChanges();
					return of(null);
				}),
			)
			.subscribe((response) => {
				if (response?.data) {
					const now = new Date();
					this.data = response.data.map((d: any) => this.toDiscountRow(d, now));
					this.totalRecords = response.total;
				}
				this.isLoading = false;
				this.cdr.detectChanges();
			});
	}

	/** Table row: who can use it, formatted value/dates and the status tag */
	private toDiscountRow(d: any, now: Date): any {
		return {
			...d,
			// who can use it: everyone, one user, or whoever has the code
			appliesTo:
				d.scope === 'USER'
					? d.userName || this.translocoService.translate('discountsCrud.appliesTo.user')
					: d.scope === 'CODE'
						? this.translocoService.translate('discountsCrud.appliesTo.code', { code: d.code })
						: this.translocoService.translate('discountsCrud.appliesTo.everyone'),
			formattedValue:
				d.type === 'PERCENTAGE' ? `${d.value}%` : formatEur(d.value, activeLocale(this.translocoService)),
			formattedStartDate: d.startDate
				? this.dateService.formatToDDMMYYYY(d.startDate)
				: this.translocoService.translate('discountsCrud.noDate'),
			formattedEndDate: d.endDate
				? this.dateService.formatToDDMMYYYY(d.endDate)
				: this.translocoService.translate('discountsCrud.noDate'),
			status: this.getDiscountStatus(d, now),
		};
	}

	onUpdateDiscount(discount: DiscountResponseDTO): void {
		if (!discount?.id) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('discountsCrud.messages.noValidId'),
			);
			return;
		}

		if (!this.crudDiscount) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('discountsCrud.messages.editNotAvailable'),
			);
			return;
		}

		this.messageAlertService.showMessage(
			'info',
			this.translocoService.translate('discountsCrud.messages.editMode'),
			this.translocoService.translate('discountsCrud.messages.editing', { name: discount.name }),
		);
		this.crudDiscount.show(discount);
	}

	onDeleteDiscount(discounts: DiscountResponseDTO | DiscountResponseDTO[]): void {
		if (!discounts || (Array.isArray(discounts) && discounts.length === 0)) {
			this.messageAlertService.showMessage(
				'error',
				this.translocoService.translate('common.labels.error'),
				this.translocoService.translate('discountsCrud.messages.noSelection'),
			);
			return;
		}

		const discountArray = Array.isArray(discounts) ? discounts : [discounts];
		const target =
			discountArray.length === 1
				? this.translocoService.translate('discountsCrud.messages.deleteTarget', {
						name: discountArray[0].name,
					})
				: this.translocoService.translate('discountsCrud.messages.deleteTargetMany', {
						count: discountArray.length,
					});

		this.confirmDialogService.confirmAndExecute(
			'delete',
			target,
			(): Observable<unknown> =>
				discountArray.length === 1
					? this.discountService.deleteDiscount(discountArray[0].id.toString())
					: this.discountService.bulkDeleteDiscounts(discountArray.map((item) => item.id)), // every selected row in one request
			{
				onSuccess: () => this.loadDiscounts(),
				errorMessage: this.translocoService.translate('discountsCrud.messages.deleteError'),
			},
		);
	}

	/** Activate/deactivate: only that row changes, so it is updated in place (no list reload) */
	onToggleActive(discount: any): void {
		const newState = !discount.isActive;
		this.discountService.updateDiscount(discount.id.toString(), { isActive: newState }).subscribe({
			next: (updated) => {
				// The update answer has no user name (the list joins it): keep the row's
				const row = this.toDiscountRow(
					{ ...discount, ...updated, userName: updated.userName ?? discount.userName },
					new Date(),
				);
				this.data = this.data.map((d) => (d.id === discount.id ? row : d));
				this.cdr.detectChanges();
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('common.labels.success'),
					this.translocoService.translate(
						newState ? 'discountsCrud.messages.activated' : 'discountsCrud.messages.deactivated',
					),
				);
			},
			error: (err) => {
				this.messageAlertService.showMessage(
					'error',
					this.translocoService.translate('common.labels.error'),
					translateApiError(err, this.translocoService, 'discountsCrud.messages.toggleError'),
				);
			},
		});
	}

	getMenuActions(discount: any): TableAction[] {
		const isActive = discount.isActive;
		return [
			{
				id: 1,
				label: this.translocoService.translate('discountsCrud.actions.edit'),
				icon: 'Edit',
				action: 'update',
				command: () => this.onUpdateDiscount(discount),
			},
			{
				id: 2,
				label: this.translocoService.translate(
					isActive ? 'discountsCrud.actions.deactivate' : 'discountsCrud.actions.activate',
				),
				icon: isActive ? 'Lock' : 'Unlock',
				action: 'toggle',
				command: () => this.onToggleActive(discount),
			},
			{
				id: 3,
				label: this.translocoService.translate('discountsCrud.actions.delete'),
				icon: 'Trash',
				action: 'delete',
				class: 'red',
				separator: true,
				command: () => this.onDeleteDiscount(discount),
			},
		];
	}

	menuActionTriggered(event: { action: string; data: DiscountResponseDTO | DiscountResponseDTO[] }): void {
		if (!event?.action || !event.data) return;

		switch (event.action) {
			case 'delete':
				this.onDeleteDiscount(event.data);
				break;
			case 'update':
				this.onUpdateDiscount(event.data as DiscountResponseDTO);
				break;
			case 'toggle':
				this.onToggleActive(event.data);
				break;
			default:
				console.warn('Unknown action:', event.action);
		}
	}

	private getDiscountStatus(d: any, now: Date): { label: string; color: string } {
		if (!d.isActive) {
			return { label: this.translocoService.translate('discountsCrud.status.inactive'), color: 'gray' };
		}
		if (d.startDate && now < new Date(d.startDate)) {
			return { label: this.translocoService.translate('discountsCrud.status.scheduled'), color: 'blue' };
		}
		if (d.endDate && now > new Date(d.endDate)) {
			return { label: this.translocoService.translate('discountsCrud.status.expired'), color: 'red' };
		}
		return { label: this.translocoService.translate('discountsCrud.status.active'), color: 'green' };
	}

	onRowSelected(discount: DiscountResponseDTO) {
		this.onUpdateDiscount(discount);
	}
}
